<template>
  <div :key="componentKey" data-testid="operatingcostinvoices-detail">
    <!-- Toolbar wie bei ContactpersonsDetail – nur in Modal ausgeblendet -->
    <DetailToolbar
      v-if="!inModal"
      :isNew="isNew"
      :editMode="editMode"
      :isAnyFieldDirty="isAnyFieldDirty"
      :onGoToList="goToList"
      :onToggleEditMode="toggleEditMode"
      :onConfirmDelete="confirmDelete"
      :headerText="
        editData?.issuerLegalName ?? t('pages.operatingcostinvoices.headlineUNewUtilitybill')
      "
    />
    <form>
      <h3 class="pt-8">{{ t('text.masterData') }}</h3>
      <SectionWrapper :class="editMode ? 'editmode' : 'viewmode'">
        <!-- 1 Element: LinkedTicketTag -->
        <ResponsiveWrapper v-if="editData.ticketId" :itemCount="1">
          <LinkedTicketTag :ticketId="editData.ticketId" />
        </ResponsiveWrapper>

        <!-- 2 Elements: Contract + Point of Sale (conditional second) -->
        <ResponsiveWrapper :itemCount="4"
          ><!-- need more space, wraps earlier-->
          <FormLazySelect
            :key="`contractId`"
            v-model="contractId"
            :data-testid="`contractId-select`"
            :disabled="isDisabled('contractId')"
            :fetchPagedData="fetchSimpleContracts"
            :fetchSingleItem="fetchContractDetails"
            :initialValue="contractId || editData.contractId || undefined"
            :isDirty="handleDirtyState('contractId')"
            :name="`contractId`"
            :viewMode="isDisabled('contractId')"
            filter
            :label="editMode ? 'selectContract' : 'contract'"
            showClear
          />
          <FormLazySelect
            v-if="showContractDependentFields"
            :key="`pointOfSale-${contractId}`"
            v-model="pointOfSaleId"
            name="pointOfSaleId"
            label="pos"
            :fetchPagedData="fetchSimplePointOfSales"
            :fetchSingleItem="fetchPointOfSaleDetails"
            :initialValue="pointOfSaleId || editData.pointOfSaleId || undefined"
            :disabled="isDisabled('pointOfSaleId')"
            :isDirty="handleDirtyState('pointOfSaleId')"
            :viewMode="isDisabled('pointOfSaleId')"
            showClear
            filter
          />
        </ResponsiveWrapper>

        <!-- <p v-if="editMode && contractId && !hasValidContractId" class="mt-2 text-sm text-red-600">
          {{ t('text.invalidContractReferenceSelectExisting') }}
        </p> -->

        <!-- 2 Elements: issuerLegalName + issueDate -->
        <ResponsiveWrapper v-if="showContractDependentFields" :itemCount="2">
          <FormInputText
            v-model="issuerLegalName"
            name="issuerLegalName"
            :placeholder="ph('issuerLegalName')"
            :isDirty="handleDirtyState('issuerLegalName')"
            :initialValue="editData.issuerLegalName"
            :disabled="isDisabled('issuerLegalName')"
            :suggestions="props.suggestions?.issuerLegalName"
            :acceptPlaceholderOnDblClick="true"
            :placeholderSource="getPlaceholderSource('issuerLegalName')"
            :acceptsPlaceholderData="true"
          />

          <FormDatePicker
            v-model="issueDate"
            name="issueDate"
            :placeholder="ph('issueDate')"
            :isDirty="handleDirtyState('issueDate')"
            :initialValue="editData.issueDate"
            :disabled="isDisabled('issueDate')"
            filter
            :acceptPlaceholderOnDblClick="true"
            :placeholderSource="getPlaceholderSource('issueDate')"
            :acceptsPlaceholderData="true"
          />
        </ResponsiveWrapper>

        <!-- 2 Elements: periodStart + periodEnd -->
        <ResponsiveWrapper v-if="showContractDependentFields" :itemCount="2">
          <FormDatePicker
            v-model="periodStart"
            name="periodStart"
            :placeholder="ph('periodStart')"
            :isDirty="handleDirtyState('periodStart')"
            :acceptsPlaceholderData="true"
            :acceptPlaceholderOnDblClick="true"
            :placeholderSource="getPlaceholderSource('periodStart')"
            :initialValue="editData.periodStart"
            :disabled="isDisabled('periodStart')"
            filter
          />
          <FormDatePicker
            v-model="periodEnd"
            name="periodEnd"
            :placeholder="ph('periodEnd')"
            :placeholderSource="getPlaceholderSource('periodEnd')"
            :acceptsPlaceholderData="true"
            :acceptPlaceholderOnDblClick="true"
            :isDirty="handleDirtyState('periodEnd')"
            :initialValue="editData.periodEnd"
            :disabled="isDisabled('periodEnd')"
            filter
          />
        </ResponsiveWrapper>
      </SectionWrapper>

      <h3 v-if="showContractDependentFields" class="pt-8">
        {{ t('text.deadlinesAndPayments') }}
      </h3>

      <SectionWrapper
        v-if="showContractDependentFields"
        :class="editMode ? 'editmode' : 'viewmode'"
      >
        <!-- 3 Elements: documentReviewDeadline + objectionDeadline + internalObjectionDeadline -->
        <ResponsiveWrapper :itemCount="3">
          <FormDatePicker
            v-model="documentReviewDeadline"
            name="documentReviewDeadline"
            :placeholder="ph('documentReviewDeadline')"
            :placeholderSource="getPlaceholderSource('documentReviewDeadline')"
            :acceptsPlaceholderData="true"
            :acceptPlaceholderOnDblClick="true"
            :isDirty="handleDirtyState('documentReviewDeadline')"
            :initialValue="editData.documentReviewDeadline"
            :disabled="isDisabled('documentReviewDeadline')"
            filter
          />
          <FormDatePicker
            v-model="objectionDeadline"
            name="objectionDeadline"
            :placeholder="ph('objectionDeadline')"
            :placeholderSource="getPlaceholderSource('objectionDeadline')"
            :acceptsPlaceholderData="true"
            :acceptPlaceholderOnDblClick="true"
            :isDirty="handleDirtyState('objectionDeadline')"
            :initialValue="editData.objectionDeadline"
            :disabled="isDisabled('objectionDeadline')"
            filter
          />
          <FormDatePicker
            v-model="internalObjectionDeadline"
            name="internalObjectionDeadline"
            :placeholder="ph('internalObjectionDeadline')"
            :placeholderSource="getPlaceholderSource('internalObjectionDeadline')"
            :acceptPlaceholderOnDblClick="true"
            :acceptsPlaceholderData="true"
            :isDirty="handleDirtyState('internalObjectionDeadline')"
            :initialValue="editData.internalObjectionDeadline"
            :disabled="isDisabled('internalObjectionDeadline')"
            filter
          />
        </ResponsiveWrapper>

        <!-- 3 Elements: isHeatingCostsCompliantHKVO (checkbox) + costsNet + vatRate -->
        <ResponsiveWrapper :itemCount="3">
          <FormCheckbox
            v-model="isHeatingCostsCompliantHKVO"
            name="isHeatingCostsCompliantHKVO"
            :placeholder="phBool('isHeatingCostsCompliantHKVO')"
            :placeholderSource="getPlaceholderSource('isHeatingCostsCompliantHKVO')"
            :acceptsPlaceholderData="true"
            :isDirty="handleDirtyState('isHeatingCostsCompliantHKVO')"
            :initialValue="editData.isHeatingCostsCompliantHKVO"
            :disabled="isDisabled('isHeatingCostsCompliantHKVO')"
          />
          <FormInputNumber
            v-model="costsNet"
            name="costsNet"
            :placeholder="ph('costsNet')"
            :placeholderSource="getPlaceholderSource('costsNet')"
            :acceptsPlaceholderData="true"
            :acceptPlaceholderOnDblClick="true"
            :isDirty="handleDirtyState('costsNet')"
            :initialValue="editData.costsNet"
            :disabled="isDisabled('costsNet')"
            :currency="currency"
            formatType="currency"
          />
          <!-- :suggestions="props.suggestions?.costsNet" -->
          <FormInputNumber
            v-model="vatRate"
            name="vatRate"
            :placeholder="ph('vatRate')"
            :placeholderSource="getPlaceholderSource('vatRate')"
            :acceptsPlaceholderData="true"
            :acceptPlaceholderOnDblClick="true"
            :isDirty="handleDirtyState('vatRate')"
            :initialValue="editData.vatRate"
            :disabled="isDisabled('vatRate')"
            :suffix="`%`"
          />
          <!-- :suggestions="props.suggestions?.vatRate" -->
        </ResponsiveWrapper>

        <ResponsiveWrapper :itemCount="3">
          <FormInputNumber
            v-model="customerPrepaymentNet"
            name="customerPrepaymentNet"
            :placeholder="ph('customerPrepaymentNet')"
            :placeholderSource="getPlaceholderSource('customerPrepaymentNet')"
            :acceptsPlaceholderData="true"
            :acceptPlaceholderOnDblClick="true"
            :isDirty="handleDirtyState('customerPrepaymentNet')"
            :initialValue="editData.customerPrepaymentNet"
            :disabled="isDisabled('customerPrepaymentNet')"
            formatType="currency"
            :currency="currency"
          />
          <!-- :suggestions="props.suggestions?.customerPrepaymentNet" -->
          <FormInputNumber
            v-model="customerPrepaymentGross"
            name="customerPrepaymentGross"
            :placeholder="ph('customerPrepaymentGross')"
            :placeholderSource="getPlaceholderSource('customerPrepaymentGross')"
            :acceptsPlaceholderData="true"
            :acceptPlaceholderOnDblClick="true"
            :isDirty="handleDirtyState('customerPrepaymentGross')"
            :initialValue="editData.customerPrepaymentGross"
            :disabled="isDisabled('customerPrepaymentGross')"
            formatType="currency"
            :currency="currency"
          />
          <FormSelect
            v-model="currency"
            name="currency"
            :placeholder="ph('currency')"
            :placeholderSource="getPlaceholderSource('currency')"
            label="currency"
            :options="Object.values(OperatingCostInvoiceV1Dto.currency)"
            :isDirty="handleDirtyState('currency')"
            :initialValue="editData?.currency || OperatingCostInvoiceV1Dto.currency.EUR"
            :disabled="!editMode"
            :acceptPlaceholderData="applyPlaceholderTrigger"
            untranslated
          />
          <!-- :suggestions="props.suggestions?.customerPrepaymentGross" -->
        </ResponsiveWrapper>

        <ResponsiveWrapper :itemCount="3">
          <FormInputNumber
            v-model="statedCustomerPrepaymentNet"
            name="statedCustomerPrepaymentNet"
            :placeholder="ph('statedCustomerPrepaymentNet')"
            :placeholderSource="getPlaceholderSource('statedCustomerPrepaymentNet')"
            :acceptsPlaceholderData="true"
            :acceptPlaceholderOnDblClick="true"
            :isDirty="handleDirtyState('statedCustomerPrepaymentNet')"
            :initialValue="editData.statedCustomerPrepaymentNet"
            :disabled="isDisabled('statedCustomerPrepaymentNet')"
            formatType="currency"
            :currency="currency"
          />
          <!-- :suggestions="props.suggestions?.statedCustomerPrepaymentNet" -->
          <FormInputNumber
            v-model="settlementResultNet"
            name="settlementResultNet"
            :placeholder="ph('settlementResultNet')"
            :placeholderSource="getPlaceholderSource('settlementResultNet')"
            :acceptsPlaceholderData="true"
            :acceptPlaceholderOnDblClick="true"
            :isDirty="handleDirtyState('settlementResultNet')"
            :initialValue="editData.settlementResultNet"
            :disabled="isDisabled('settlementResultNet')"
            formatType="currency"
            :currency="currency"
          />
          <!-- :suggestions="props.suggestions?.settlementResultNet" -->
          <FormInputNumber
            v-model="settlementResultGross"
            name="settlementResultGross"
            :placeholder="ph('settlementResultGross')"
            :placeholderSource="getPlaceholderSource('settlementResultGross')"
            :acceptsPlaceholderData="true"
            :acceptPlaceholderOnDblClick="true"
            :isDirty="handleDirtyState('settlementResultGross')"
            :initialValue="editData.settlementResultGross"
            :disabled="isDisabled('settlementResultGross')"
            formatType="currency"
            :currency="currency"
          />
          <!-- :suggestions="props.suggestions?.settlementResultGross" -->
        </ResponsiveWrapper>

        <DetailFormfooter
          v-if="!isNew"
          :isNew="isNew"
          :editMode="editMode"
          :isAnyFieldDirty="isAnyFieldDirty"
          :onGoToList="goToList"
          :onToggleEditMode="toggleEditMode"
          :onSubmit="submitForm"
          :onConfirmResetChanges="confirmResetChanges"
        >
          <ApplyAllSuggestionsButton
            v-if="showContractDependentFields && hasSuggestionsToApply"
            :onApplyAllSuggestions="handleApplyAllSuggestions"
            :suggestionsSource="props.templateSource"
            :hasSuggestionsToApply="hasSuggestionsToApply"
          />
        </DetailFormfooter>
      </SectionWrapper>

      <div v-if="!isNew && allowOperatingCostInvoiceLines">
        <h3 class="pt-8">{{ t('text.tapOverview') }}</h3>
        <SectionWrapper>
          <TapOverview
            :costsNet="costsNet"
            :statedCustomerPrepaymentNet="statedCustomerPrepaymentNet"
            :settlementResultNet="settlementResultNet"
            :customerPrepaymentGross="customerPrepaymentGross"
            :lineItems="editData.lineItems ?? []"
            :currency="currency"
          />
        </SectionWrapper>
      </div>

      <div v-if="!isNew && allowOperatingCostInvoiceLines">
        <h3 class="pt-8">{{ t('text.invoiceLineItems') }}</h3>
        <SectionWrapper>
          <OperatingCostInvoiceLines
            :invoiceId="id"
            :isNew="isNew"
            :editMode="editMode"
            :existingOperatingCostInvoiceLines="editData.lineItems ?? []"
            :onUpdateLineItemsOnCreate="
              (data: OperatingCostInvoiceLineItemSimple[]) => (lineItemsToCreate = data)
            "
            :fetchData="fetchData"
            :latestInvoiceId="latestInvoiceIdForLines"
            :latestInvoiceLineItems="latestInvoiceLineItemsForLines"
            :importSourceInvoices="props.importSourceInvoices"
            :currentContractId="effectiveContractId"
            :currency="currency"
            :selectedImportSourceInvoiceId="props.selectedImportSourceInvoiceId"
            :daysInBillingPeriod="daysInBillingPeriod"
            @lineItemCreated="onLineItemCreated"
          />
        </SectionWrapper>
      </div>

      <DetailFormfooter
        v-if="isNew"
        :isNew="isNew"
        :editMode="editMode"
        :isAnyFieldDirty="isAnyFieldDirty"
        :onGoToList="goToList"
        :onToggleEditMode="toggleEditMode"
        :onSubmit="submitForm"
        :onConfirmResetChanges="confirmResetChanges"
      >
        <ApplyAllSuggestionsButton
          v-if="showContractDependentFields && hasSuggestionsToApply"
          :onApplyAllSuggestions="handleApplyAllSuggestions"
          :suggestionsSource="props.templateSource"
          :hasSuggestionsToApply="hasSuggestionsToApply"
        />
      </DetailFormfooter>
    </form>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import ApplyAllSuggestionsButton from '@/components/buttons/ApplyAllSuggestionsButton.vue';
import type {
  DetailFormDisableProps,
  DetailFormExtraProps,
} from '@/components/details/detailTypes';
import {
  DetailNavigationInlinePropsDefaults,
  useDetailNavigation,
  type UseDetailNavigationInlineProps,
} from '@/components/details/hooks/useDetailNavigation';
import { useFormDirtyState } from '@/components/forms/hooks/useFormDirtyState';
import { useFieldDisabling } from '@/components/forms/hooks/useFormFieldDisable';
import { useFormPrefill } from '@/components/forms/hooks/useFormPrefill';
import type {
  LazySelectFetchOptions,
  PaginatedResponse,
} from '@/components/forms/hooks/useLazySelect.ts';
import LinkedTicketTag from '@/components/LinkedTicketTag.vue';
import SectionWrapper from '@/components/section-wrapper/SectionWrapper.vue';
import { useContracts } from '@/views/contracts/hooks/useContracts';
import OperatingCostInvoiceLines from '@/views/operatingcostinvoices/components/operationcostinvoicelines/OperatingCostInvoiceLines.vue';
import type {
  InvoiceItemSuggestionSource,
  OperatingCostInvoiceLineItemSimple,
} from '@/views/operatingcostinvoices/components/operationcostinvoicelines/operatingCostInvoiceTypes';
import { getOperatingCostInvoiceSchema } from '@/views/operatingcostinvoices/configs/operatingCostInvoiceFormConfig';
import {
  useOperatingCostInvoices,
  useOperatingCostInvoicesConverter,
} from '@/views/operatingcostinvoices/hooks/useOperatingCostInvoices';
import type { OperatingCostInvoiceSimple } from '@/views/operatingcostinvoices/operatingCostInvoiceTypes';
import { usePointOfSales } from '@/views/pointofsales/hooks/usePointOfSales';
import type { TicketEnriched } from '@/views/tickets/ticketsTypes';
import { useDetailView } from '@/composables/useDetailView';
import {
  type ContractSimpleV2Dto,
  type ContractV1Dto,
  OperatingCostInvoiceV1Dto,
  OrderByRelationalPropertyDto,
  type PointOfSaleSimpleV2Dto,
  type PointOfSaleV1Dto,
} from '@/openapi';

import TapOverview from './components/operationcostinvoicelines/tap-overview/TapOverview.vue';

const props = withDefaults(
  defineProps<
    UseDetailNavigationInlineProps &
      DetailFormExtraProps<OperatingCostInvoiceSimple> &
      DetailFormDisableProps<OperatingCostInvoiceSimple> & {
        debugMountContext?: string;
        ticket?: TicketEnriched | null;
        contracts?: ContractSimpleV2Dto[];
        latestInvoiceId?: string | null;
        isNew?: boolean;
        latestInvoiceLineItems?: OperatingCostInvoiceLineItemSimple[];
        isSubComponent?: boolean;
        importSourceInvoices?: OperatingCostInvoiceSimple[];
        templateSource?: InvoiceItemSuggestionSource;
        allowOperatingCostInvoiceLines?: boolean;
        selectedImportSourceInvoiceId?: string; // ID of selected invoice from importSourceInvoices
      }
  >(),
  {
    ...DetailNavigationInlinePropsDefaults,
    debugMountContext: undefined,
    initialData: undefined,
    placeholders: undefined,
    allDisabled: undefined,
    disabledFields: undefined,
    suggestions: undefined,
    ticket: undefined,
    contracts: undefined,
    latestInvoiceId: undefined,
    latestInvoiceLineItems: undefined,
    isSubComponent: undefined,
    importSourceInvoices: undefined,
    templateSource: undefined,
    allowOperatingCostInvoiceLines: true,
    selectedImportSourceInvoiceId: undefined,
  },
);

const emit = defineEmits<{
  (
    e: 'submitSuccess',
    payload?: {
      id?: string;
      request?: OperatingCostInvoiceSimple;
      resp?: OperatingCostInvoiceSimple;
    },
  ): void;
  (e: 'contractIdChange', contractId: string | null): void;
  (e: 'requestToggleEditMode', next: boolean): void;
  (e: 'requestGoToList'): void;
  (e: 'requestGoToSubcomponentList'): void;
  (e: 'lineItemsCreated', payload: { ok: boolean }): void;
  (e: 'invoiceLoaded', invoice: OperatingCostInvoiceSimple): void;
}>();

const { t } = useI18n();
const route = useRoute();
const inModal = computed(() => props.inModal ?? false);

const { fetchPointOfSalesSimple, fetchPointOfSale } = usePointOfSales();
const {
  fetchOperatingCostInvoice,
  createOperatingCostInvoice,
  updateOperatingCostInvoice,
  deleteOperatingCostInvoice,
} = useOperatingCostInvoices();
const {
  convertOperatingCostInvoiceDtoToOperatingCostInvoiceSimple,
  convertOperatingCostInvoiceSimpleToCreateOperatingCostInvoiceDto,
  convertOperatingCostInvoiceSimpleToUpdateOperatingCostInvoiceDto,
  fetchLatestByContractAndConvertToSimple,
} = useOperatingCostInvoicesConverter();

const { id, isNew, editMode, mainComponentID, goToList, toggleEditMode } = useDetailNavigation({
  isSubComponent: computed(() => props.isSubComponent ?? false).value,
  uuidFromProp: computed(() => props.uuid),
  isNewFromProp: computed(() => props.isNew),
  editModeFromProp: computed(() => props.editMode),
});

const { isAnyFieldDirty, handleDirtyState } = useFormDirtyState();
const isContractReferenceInvalid = ref(false);
const hasValidContractId = computed(() => !!contractId.value && !isContractReferenceInvalid.value);
const showContractDependentFields = computed(() => !editMode.value || hasValidContractId.value);

const operatingCostInvoice = ref<OperatingCostInvoiceSimple>();
const editData = ref<Partial<OperatingCostInvoiceSimple>>({});
const lineItemsToCreate = ref<OperatingCostInvoiceLineItemSimple[]>([]);
const applyPlaceholderTrigger = ref(0); // Trigger to signal FormSelects to apply placeholders

const { fetchContractsSimplePaginated, fetchContract } = useContracts();

const latestInvoiceIdLocal = ref<string | null>(props.latestInvoiceId ?? null);
const latestInvoiceLineItemsLocal = ref<OperatingCostInvoiceLineItemSimple[]>(
  props.latestInvoiceLineItems ?? [],
);

const effectiveContractId = computed<string | null>(() => {
  if (editMode.value && !hasValidContractId.value) return null;
  return contractId.value || editData.value.contractId || props.initialData?.contractId || null;
});

const latestInvoiceIdForLines = computed(() => {
  return props.latestInvoiceId ?? latestInvoiceIdLocal.value ?? undefined;
});

const latestInvoiceLineItemsForLines = computed(() => {
  return props.latestInvoiceLineItems ?? latestInvoiceLineItemsLocal.value ?? [];
});

const daysInBillingPeriod = computed<number | undefined>(() => {
  // Only calculate for existing invoices, not for new ones
  if (isNew.value || !periodStart.value || !periodEnd.value) return;

  const startDate = new Date(periodStart.value);
  const endDate = new Date(periodEnd.value);

  // Use UTC midnight values to avoid timezone/DST edge cases.
  const startUtc = Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const endUtc = Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  const diffDays = Math.floor((endUtc - startUtc) / (1000 * 60 * 60 * 24));

  // Inclusive day count: both period start and period end are counted.
  return diffDays + 1;
});

const { handleSubmit, setValues, resetForm } = useForm<OperatingCostInvoiceSimple>({
  validationSchema: getOperatingCostInvoiceSchema(t),
  validateOnMount: false,
});

const { watchInitialData } = useFormPrefill<OperatingCostInvoiceSimple>({
  initialData: computed(() => props.initialData ?? undefined),
  setValues,
  resetForm: (args) => resetForm(args),
  isNew,
  isDirty: () => isAnyFieldDirty.value,
  forceOnChange: true,
});

const { isDisabled } = useFieldDisabling<OperatingCostInvoiceSimple>({
  editMode,
  allDisabled: computed(() => props.allDisabled),
  disabledFields: computed(() => props.disabledFields),
});

/** Compute placeholder source for any given field */
const getPlaceholderSource = (fieldName: string): InvoiceItemSuggestionSource | undefined => {
  if (!props.placeholders?.[fieldName as keyof OperatingCostInvoiceSimple]) {
    return undefined; // no placeholder → no source icon
  }
  return props.templateSource;
};

const submitForm = handleSubmit(onSubmit);

const { value: contractId } = useField<string>('contractId');
const { value: pointOfSaleId } = useField<string>('pointOfSaleId');
const { value: issuerLegalName } = useField<string>('issuerLegalName');
const { value: issueDate } = useField<string | null>('issueDate');
const { value: periodStart } = useField<string | null>('periodStart');
const { value: periodEnd } = useField<string | null>('periodEnd');

const { value: documentReviewDeadline } = useField<string | undefined>('documentReviewDeadline');
const { value: objectionDeadline } = useField<string | undefined>('objectionDeadline');
const { value: internalObjectionDeadline } = useField<string | null>('internalObjectionDeadline');
const { value: isHeatingCostsCompliantHKVO } = useField<boolean>('isHeatingCostsCompliantHKVO');
const { value: costsNet } = useField<number | undefined>('costsNet');
const { value: vatRate } = useField<number>('vatRate');
const { value: customerPrepaymentNet } = useField<number | undefined>('customerPrepaymentNet');
const { value: customerPrepaymentGross } = useField<number | undefined>('customerPrepaymentGross');
const { value: currency } = useField<OperatingCostInvoiceV1Dto.currency>('currency');
const { value: statedCustomerPrepaymentNet } = useField<number | undefined>(
  'statedCustomerPrepaymentNet',
);
const { value: settlementResultNet } = useField<number | undefined>('settlementResultNet');
const { value: settlementResultGross } = useField<number | undefined>('settlementResultGross');

function confirmResetChanges() {
  setValues(editData.value);
}

/**
 * Check if a field value is truly empty
 * Returns true only for: null, undefined, empty string, whitespace-only string, 0
 */
function isFieldEmpty(value: any): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  if (typeof value === 'number') {
    return false; // Numbers are never "empty" - 0 is a valid value
  }
  return false;
}

/**
 * Apply all available suggestions to empty fields
 * Only fills fields that are empty (not in display AND not in model) and have a placeholder value
 */
function handleApplyAllSuggestions() {
  const fieldsToUpdate: Partial<OperatingCostInvoiceSimple> = {};

  // Map of field names to their current useField().value refs (string/number fields)
  const fieldValueMap: Record<string, any> = {
    issuerLegalName,
    issueDate,
    periodStart,
    periodEnd,
    documentReviewDeadline,
    objectionDeadline,
    internalObjectionDeadline,
    costsNet,
    vatRate,
    customerPrepaymentNet,
    customerPrepaymentGross,
    currency,
    statedCustomerPrepaymentNet,
    settlementResultNet,
    settlementResultGross,
  };

  // Iterate through all fields and fill empty ones with placeholders
  for (const [fieldName, fieldRef] of Object.entries(fieldValueMap)) {
    const currentFieldValue = fieldRef.value; // Current value in the form input
    const placeholder = props.placeholders?.[fieldName as keyof OperatingCostInvoiceSimple];

    // Only fill if field is truly empty AND placeholder exists
    if (placeholder && isFieldEmpty(currentFieldValue)) {
      (fieldsToUpdate as any)[fieldName] = placeholder;
    }
  }

  // Handle boolean fields (checkbox)
  const currentCheckboxValue = isHeatingCostsCompliantHKVO.value;
  const checkboxPlaceholder = phBool('isHeatingCostsCompliantHKVO'); // treats booleans

  // For boolean fields: Apply suggestion if placeholder exists AND value is different from placeholder
  // OR if current value is null/undefined (truly unset)
  const shouldApplyCheckboxSuggestion =
    checkboxPlaceholder !== null &&
    checkboxPlaceholder !== undefined &&
    (currentCheckboxValue === null ||
      currentCheckboxValue === undefined ||
      currentCheckboxValue !== checkboxPlaceholder);

  if (shouldApplyCheckboxSuggestion) {
    fieldsToUpdate.isHeatingCostsCompliantHKVO = checkboxPlaceholder;
  }

  // Apply all updates at once
  setValues(fieldsToUpdate);

  // Signal FormSelects to apply their placeholders if not user-modified
  applyPlaceholderTrigger.value++;
}

/**
 * Check if there are any empty fields that have suggestions
 * Used to show/hide the "Apply All Suggestions" button
 */
const hasSuggestionsToApply = computed(() => {
  // Map of field names to their current useField().value refs
  const fieldValueMap: Record<string, any> = {
    issuerLegalName,
    issueDate,
    periodStart,
    periodEnd,
    documentReviewDeadline,
    objectionDeadline,
    internalObjectionDeadline,
    costsNet,
    vatRate,
    customerPrepaymentNet,
    customerPrepaymentGross,
    currency,
    statedCustomerPrepaymentNet,
    settlementResultNet,
    settlementResultGross,
  };

  // Check if any field is empty and has a suggestion
  for (const [fieldName, fieldRef] of Object.entries(fieldValueMap)) {
    const currentFieldValue = fieldRef.value;
    const placeholder = props.placeholders?.[fieldName as keyof OperatingCostInvoiceSimple];

    // If field is empty AND has a placeholder → there's something to apply
    if (placeholder && isFieldEmpty(currentFieldValue)) {
      return true;
    }
  }

  // Check if checkbox has a suggestion to apply
  const currentCheckboxValue = isHeatingCostsCompliantHKVO.value;
  const checkboxPlaceholder = phBool('isHeatingCostsCompliantHKVO'); // treats booleans
  // For boolean fields: has suggestion if placeholder exists AND value differs or is unset
  const shouldApplyCheckboxSuggestion =
    checkboxPlaceholder !== null &&
    checkboxPlaceholder !== undefined &&
    (currentCheckboxValue === null ||
      currentCheckboxValue === undefined ||
      currentCheckboxValue !== checkboxPlaceholder);

  if (shouldApplyCheckboxSuggestion) {
    return true;
  }

  return false; // No empty fields with suggestions
});

async function confirmDelete() {
  await deleteOperatingCostInvoice(id.value);
  goToList();
}

async function fetchSimpleContracts(
  options: LazySelectFetchOptions,
): Promise<PaginatedResponse<ContractSimpleV2Dto> | undefined> {
  const result = await fetchContractsSimplePaginated({
    searchString: options.searchString ?? '',
    page: options.page,
    limit: options.limit,
  });

  return result;
}

async function fetchContractDetails(id: string): Promise<ContractV1Dto | null | undefined> {
  try {
    // Validate that we have a valid ID before fetching
    if (!id || typeof id !== 'string' || id.trim() === '') {
      return null;
    }
    return await fetchContract(id);
  } catch (err) {
    console.error('Error fetching contract details:', err);
    return null;
  }
}

async function fetchSimplePointOfSales(
  options: LazySelectFetchOptions,
): Promise<PaginatedResponse<PointOfSaleSimpleV2Dto> | undefined> {
  try {
    // Use the contractId parameter directly in the API call
    const result = await fetchPointOfSalesSimple(
      [{ propertyName: 'name', value: OrderByRelationalPropertyDto.value.ASC }],
      undefined,
      options.searchString ?? undefined,
      options.page,
      options.limit,
      contractId.value ?? undefined,
    );

    return result;
  } catch (err) {
    console.error('Error fetching point of sales:', err);
    // Return empty response structure on error
    return { data: [], count: 0, pages: 0, page: options.page, limit: options.limit };
  }
}

async function fetchPointOfSaleDetails(id: string): Promise<PointOfSaleV1Dto | null | undefined> {
  try {
    // Validate that we have a valid ID before fetching
    if (!id || typeof id !== 'string' || id.trim() === '') {
      return null;
    }
    return await fetchPointOfSale(id);
  } catch (err) {
    console.error('Error fetching point of sale details for ID:', id, err);
    return null;
  }
}

async function onSubmit(values: OperatingCostInvoiceSimple) {
  if (!isNew.value && id.value) {
    const updateDto = convertOperatingCostInvoiceSimpleToUpdateOperatingCostInvoiceDto(values);

    // Add ticketId if available from props
    if (props.ticket?.id) {
      updateDto.ticketId = props.ticket.id;
    }

    const resp = await updateOperatingCostInvoice(String(id.value), updateDto);

    emit('submitSuccess', {
      id: resp.id,
      request: values,
      resp: convertOperatingCostInvoiceDtoToOperatingCostInvoiceSimple(resp),
    });

    if (props.inModal) {
      return;
    }

    toggleEditMode();
    await fetchData();
  } else {
    const createDto = convertOperatingCostInvoiceSimpleToCreateOperatingCostInvoiceDto(
      values,
      lineItemsToCreate.value,
    );

    // Add ticketId if available from props
    if (props.ticket?.id) {
      createDto.ticketId = props.ticket.id;
    }
    const resp = await createOperatingCostInvoice(createDto);

    emit('submitSuccess', {
      id: resp.id,
      request: values,
      resp: convertOperatingCostInvoiceDtoToOperatingCostInvoiceSimple(resp),
    });

    goToList();
  }
}

/** placeholder helper for string fields */
const ph = (key: keyof OperatingCostInvoiceSimple, fallback?: string) =>
  props.placeholders?.[key] ?? fallback ?? '';

/** placeholder helper for boolean fields (checkbox) */
const phBool = (
  key: keyof OperatingCostInvoiceSimple,
  fallback?: boolean | null,
): boolean | null => {
  const value: string | boolean | null | undefined = props.placeholders?.[key];

  return `${value}` === 'true' ? true : `${value}` === 'false' ? false : (fallback ?? null);
};

const fetchData = async () => {
  // 1) Prefill for NEW records - completely blocks fetchData
  if (isNew.value && editData.value && Object.keys(editData.value).length > 0) {
    // Prefill bereits angewendet → NICHT fetchData aufrufen
    return;
  }

  // 2) Without ID → no fetch
  if (!id.value) return;

  // 3) Normal fetch for existing records
  try {
    const data = await fetchOperatingCostInvoice(String(id.value));
    const simple = data && convertOperatingCostInvoiceDtoToOperatingCostInvoiceSimple(data);

    operatingCostInvoice.value = simple || undefined;
    editData.value = { ...(simple || {}) };

    resetForm({ values: { ...(simple || {}) } });
  } catch (err) {
    console.error('Error fetching operating cost invoice:', err);

    // Reset to empty state on error
    operatingCostInvoice.value = undefined;
    editData.value = {};
  }
};

const clearValues = () => {
  operatingCostInvoice.value = undefined;
  editData.value = {};
};

const { componentKey } = useDetailView({
  fetchData,
  clearValues,
  isSubComponent: computed(() => props.isSubComponent ?? false).value,
});

watchInitialData((merged) => {
  // Keep local state in sync
  // This should only run for NEW invoices due to the isNew check in useFormPrefill
  // For existing invoices being edited, this won't run to preserve user changes
  operatingCostInvoice.value = merged;
  editData.value = { ...merged };
});

watch(
  contractId,
  async (newContractId, oldContractId) => {
    emit('contractIdChange', newContractId || null);

    // Only process if this is an actual change, not the initial load
    const isActualChange = oldContractId !== undefined && newContractId !== oldContractId;

    if (isActualChange) {
      // Update editData with the new contractId to ensure it's in sync
      if (editData.value) {
        editData.value.contractId = newContractId || '';
      }

      // Clear point of sale since it's contract-dependent
      // Clear both the field value AND editData to ensure the FormLazySelect is fully reset
      pointOfSaleId.value = '';
      if (editData.value) {
        editData.value.pointOfSaleId = '';
      }

      // For existing invoices, we want to preserve all other field values
      // The form dirty state will track that the user has made changes
    }

    // When contract is cleared completely
    if (!newContractId) {
      isContractReferenceInvalid.value = false;
      latestInvoiceIdLocal.value = null;
      latestInvoiceLineItemsLocal.value = [];
      return;
    }

    const contractDetails = await fetchContractDetails(String(newContractId));
    isContractReferenceInvalid.value = !contractDetails;

    if (isContractReferenceInvalid.value) {
      latestInvoiceIdLocal.value = null;
      latestInvoiceLineItemsLocal.value = [];
      return;
    }

    // For existing invoices, DON'T fetch latest invoice data or reset anything
    // The user is editing an existing invoice and changing the contract
    // We should preserve all their other edits
    if (!isNew.value) {
      return;
    }

    // Only for NEW invoices: fetch the latest invoice for this contract
    // to help pre-fill line items
    const latest = await fetchLatestByContractAndConvertToSimple(newContractId);

    if (!latest) {
      latestInvoiceIdLocal.value = null;
      latestInvoiceLineItemsLocal.value = [];
      return;
    }

    latestInvoiceIdLocal.value = latest.id ?? null;
    latestInvoiceLineItemsLocal.value = latest.lineItems ?? [];
  },
  { immediate: true },
);

onMounted(() => {
  // 1) Prefer contractId from initialData (e.g. wrapper/template logic)
  if (props.initialData?.contractId) {
    contractId.value = String(props.initialData.contractId);
    return;
  }

  // 2) If there is exactly one contract via props, use it
  if (props.contracts && props.contracts.length === 1 && props.contracts[0]) {
    contractId.value = String(props.contracts[0].id);
    return;
  }

  // 3) Use mainComponentID only in explicit contracts route context.
  //    This avoids accidentally using a ticket UUID as contractId when ticket data
  //    is not available yet during initial mount.
  const isContractsRouteContext =
    String(route.params.mainComponent ?? '').toLowerCase() === 'contracts';
  if (props.isSubComponent && mainComponentID.value && !props.ticket && isContractsRouteContext) {
    contractId.value = String(mainComponentID.value);
    return;
  }

  // 4) Otherwise: contractId will be loaded from the invoice data in fetchData
  //    or user has to select manually for new invoices
});

/**
 * Handler: LineItem successfully created
 * Propagate to parent (wrapper/plugin)
 */
const onLineItemCreated = (payload: { ok: boolean }) => {
  emit('lineItemsCreated', payload);
};
</script>
